import { endOfDay, startOfDay, formatDuration, intervalToDuration, Interval, subDays, addDays, hoursToMilliseconds } from 'date-fns'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Timeline } from '@mui/icons-material'
import { Box, Card, Grid, TableContainer } from '@mui/material'

import { useGetTrackedEventQuery } from '../../../../../api/core'
import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { GamePerformaceDialogData, GamePerformanceDialog } from '../../../../../components/GamePerformanceDialog/GamePerformanceDialog'
import { PerformanceChartV2DataType } from '../../../../../components/PerformanceChartV2/PerformanceChartV2DataType'
import { formatEventInstanceDuration, getIntervalByGranularity } from '../../../../../helpers/date'
import { endOfDayTimestamp } from '../../../../../helpers/endOfDayTimestamp'
import { useError } from '../../../../../hooks/useError'
import { ActionButton } from '../../../../../internal/components/ActionButton/ActionButton'
import { EstimatePlatformType } from '../../../../estimates/types/EstimatePlatformType'
import { useCurrentMarket } from '../../../../markets'
import { GranularityValue } from '../../../../revenue-and-downloads/types/Filters'
import { useGameUpdatePerformanceData } from '../../../../update-history/hooks/useGameUpdatePerformanceData'
import { useDateTimeFormatter } from '../../../hooks/useDateTimeFormatter'
import { useEventInstanceDurations } from '../../../hooks/useEventInstanceDurations'
import { useTrackedEventsEndTimestamp } from '../../../hooks/useTrackedEventsEndTimestamp'
import { EventInstanceDuration } from '../../../types/EventInstanceDuration'
import { TrackedGame } from '../../../types/TrackedGame'
import { Duration, TrackingEventByGame } from '../../../types/TrackingEvents'
import { PerformanceDialogEventsContainer } from '../../PerformanceDialogEventsContainer/PerformanceDialogEventsContainer'
import { EventHeader } from '../EventHeader/EventHeader'

type InstancesTabProps = {
  currentTrackedGame?: TrackedGame
  duration?: Duration
  eventId?: string
  eventTypeId?: string
  onEventHighlighted?: (eventId: string) => void
  trackedEvents: TrackingEventByGame
  trackedGames?: TrackedGame[]
  children?: ReactNode
}

const dateFormat: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

export const InstancesTab: FC<InstancesTabProps> = ({
  currentTrackedGame,
  duration,
  eventId,
  eventTypeId,
  onEventHighlighted,
  trackedEvents,
  trackedGames,
}) => {
  const { t } = useTranslation()
  const dateYesterday = subDays(new Date(), 1)
  const dateFormatter = useDateTimeFormatter(dateFormat)
  const eventQuery = useGetTrackedEventQuery(eventId as string, { skip: !eventId })
  useError({ error: eventQuery.error })
  const eventInstanceDurations = useEventInstanceDurations(eventQuery.data?.durations)
  const isLoading = eventQuery.isLoading || eventQuery.isFetching

  const [gamePerformanceDialogData, setGamePerformanceDialogData] = useState<GamePerformaceDialogData>()
  const [initialSelectedPerformanceChartInterval, setInitialSelectedPerformanceChartInterval] = useState<Interval>()
  const [selectedPerformanceChartInterval, setSelectedPerformanceChartInterval] = useState<Interval>()
  const [eventPeriodToHighlight, setEventPeriodToHighlight] = useState<Interval>()
  const handlePerformanceDialogClose = useCallback(() => {
    setGamePerformanceDialogData(undefined)
    setSelectedPerformanceChartInterval(undefined)
    setEventPeriodToHighlight(undefined)
  }, [])
  const { currentMarketIso } = useCurrentMarket()

  // Get performance data to check availability for each instance
  const performanceData = useGameUpdatePerformanceData({
    appId: currentTrackedGame?.game.appId || 0,
    gameId: currentTrackedGame?.game.id,
    marketIso: currentMarketIso,
    rollingDays: 1,
    granularity: GranularityValue.Day,
    platformTypes: [EstimatePlatformType.Phone, EstimatePlatformType.Tablet],
  })

  const gamePerformanceDialogSelectedUpdates = useMemo(() => {
    return selectedPerformanceChartInterval?.start ? [{ timestamp: selectedPerformanceChartInterval?.start }] : []
  }, [selectedPerformanceChartInterval?.start])

  // Create highlighted marks for the event period
  const gamePerformanceDialogEventPeriodMarks = useMemo(() => {
    if (!eventPeriodToHighlight) return []

    const eventStart = typeof eventPeriodToHighlight.start === 'number' ? eventPeriodToHighlight.start : new Date(eventPeriodToHighlight.start).getTime()
    const eventEnd = typeof eventPeriodToHighlight.end === 'number' ? eventPeriodToHighlight.end : new Date(eventPeriodToHighlight.end).getTime()
    const differenceInTime = eventEnd - eventStart

    // For single-day events
    if (differenceInTime <= hoursToMilliseconds(24)) {
      return [{ timestamp: eventStart }]
    }

    // For multi-day events, normalize the end timestamp to appear at the start of the correct day
    // Extract UTC date and use end of previous day (which is start of the target day on chart)
    const endDate = new Date(eventEnd)
    const normalizedEnd = new Date(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate() - 1, 23, 59, 59, 999).getTime()

    return [{ timestamp: eventStart }, { timestamp: normalizedEnd }]
  }, [eventPeriodToHighlight])

  const handleChartClick = setSelectedPerformanceChartInterval
  const onOpenPerformanceChart = useCallback(
    (start: number, end: number, trackedGame: TrackedGame | undefined) => {
      if (trackedGame) {
        setEventPeriodToHighlight({ start, end })

        // Calculate granularity based on the event duration, not the extended viewing window
        const eventDuration = end - start
        const eventDurationInDays = Math.round(eventDuration / hoursToMilliseconds(24))
        const granularityValue = eventDurationInDays > 60 ? GranularityValue.Week : GranularityValue.Day

        // Extend the date range by 15 days before and after the event period for the viewing window
        const fifteenDaysBeforeStart = subDays(new Date(start), 15).getTime()
        const fifteenDaysAfterEnd = addDays(new Date(end), 15).getTime()

        // Cap the start date at first available data and end date at today to avoid showing empty chart regions
        let extendedStart = fifteenDaysBeforeStart
        let extendedEnd = Math.min(fifteenDaysAfterEnd, endOfDay(new Date()).getTime())

        if (performanceData.estimates && performanceData.estimates.length > 0) {
          const timestamps = performanceData.estimates.map((estimate) => estimate.ts)
          const firstDataTimestamp = Math.min(...timestamps)
          extendedStart = Math.max(fifteenDaysBeforeStart, startOfDay(new Date(firstDataTimestamp)).getTime())
        }

        setInitialSelectedPerformanceChartInterval({ start: extendedStart, end: extendedEnd })
        setGamePerformanceDialogData({
          appId: trackedGame.game.appId,
          gameName: trackedGame.game.resolvedName,
          marketIso: currentMarketIso,
          initialYAxisLeftConfig: { dataType: PerformanceChartV2DataType.Revenue },
          initialYAxisRightConfig: { dataType: PerformanceChartV2DataType.Downloads },
          initialGranularity: granularityValue,
          highlightedPeriod: { start, end },
        })
      }
    },
    [currentMarketIso, performanceData.estimates]
  )

  const getPerformanceDialogInstanceTableInterval = useCallback(
    (granularity: GranularityValue) => {
      return selectedPerformanceChartInterval
        ? selectedPerformanceChartInterval
        : gamePerformanceDialogData?.highlightedTimestamp
        ? getIntervalByGranularity(granularity, gamePerformanceDialogData?.highlightedTimestamp)
        : undefined
    },
    [gamePerformanceDialogData?.highlightedTimestamp, selectedPerformanceChartInterval]
  )

  // Function to check if performance data is available for a specific time period
  const hasPerformanceDataForPeriod = useCallback(
    (startDate: number, endDate: number): boolean => {
      if (!performanceData.estimates || performanceData.estimates.length === 0) {
        return false
      }

      // Check if there's any estimate data within the time period
      return performanceData.estimates.some((estimate) => estimate.ts >= startDate && estimate.ts <= endDate)
    },
    [performanceData.estimates]
  )

  const maxEndTimestamp = useTrackedEventsEndTimestamp(
    eventInstanceDurations?.reduce((max, current) => Math.max(max, current.end), endOfDayTimestamp()) ?? endOfDayTimestamp()
  )

  const cappedEventInstanceDurations = useMemo(() => {
    return (eventInstanceDurations ?? [])
      .map((instance) => {
        const end = Math.min(instance.end, maxEndTimestamp)

        const interval = instance.interval
          ? (() => {
              const iEnd = Math.min(instance.interval.end, maxEndTimestamp)
              return iEnd >= instance.interval.start ? { ...instance.interval, end: iEnd } : undefined
            })()
          : undefined

        return { ...instance, end, interval }
      })
      .filter((instance) => instance.start < maxEndTimestamp && instance.end > instance.start)
  }, [eventInstanceDurations, maxEndTimestamp])

  const eventInstanceColumns: GRTableColumn<EventInstanceDuration, typeof customProps>[] = useMemo(
    () => [
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:duration_start'),
            accessor: ({ row }) => dateFormatter.format(row.start),
            cellProps: { align: 'left' },
            sortable: true,
            sortOrder: SortOrder.DESC,
            sortAccessor: ({ row }) => row.start,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:duration_end'),
            accessor: ({ row }) => dateFormatter.format(row.end),
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => row.end,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:duration'),
            accessor: ({ row }) => {
              return formatEventInstanceDuration(row.start, row.end)
            },
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => endOfDay(row.end).getTime() - startOfDay(row.start).getTime(),
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:interval'),
            accessor: ({ row, customTableProps }) => {
              const intervalLength = (row.interval?.end || 0) - (row.interval?.start || 0)
              const isAfterYesterday = customTableProps?.dateYesterday ? endOfDay(row.start) > customTableProps?.dateYesterday : false
              const hasPerformanceData = customTableProps?.hasPerformanceDataForPeriod ? customTableProps.hasPerformanceDataForPeriod(row.start, row.end) : true
              const noDataAvailable = isAfterYesterday || !hasPerformanceData
              const isMobileGame = customTableProps?.currentTrackedGame ? customTableProps?.currentTrackedGame.game.isMobileGame() : false
              return (
                <Grid container sx={{ alignItems: 'center' }}>
                  {row.interval &&
                    (intervalLength === 0 ? (
                      <Grid item>{t('live-events:zero_interval')}</Grid>
                    ) : (
                      <Grid item>{formatDuration(intervalToDuration(row.interval), { format: ['years', 'months', 'days', 'hours'] })}</Grid>
                    ))}
                  {isMobileGame && (
                    <Grid item sx={{ marginLeft: 'auto' }}>
                      <GRTooltip content={noDataAvailable ? t('live-events:no_data_available_for_this_time_period') : t('top-games:show_performance')}>
                        <ActionButton
                          onClick={() => customTableProps?.onOpenPerformanceChart(row.start, row.end, customTableProps.currentTrackedGame)}
                          disabled={noDataAvailable}
                        >
                          <Timeline fontSize="small" />
                        </ActionButton>
                      </GRTooltip>
                    </Grid>
                  )}
                </Grid>
              )
            },
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => (row.interval ? endOfDay(row.interval.end).getTime() - startOfDay(row.interval.start).getTime() : 0),
          },
        ],
      },
    ],
    [dateFormatter, t]
  )

  const customProps = useMemo(
    () => ({
      currentTrackedGame,
      dateYesterday,
      onOpenPerformanceChart,
      hasPerformanceDataForPeriod,
    }),
    [currentTrackedGame, dateYesterday, onOpenPerformanceChart, hasPerformanceDataForPeriod]
  )

  const [columns, setColumns] = useState<GRTableColumn<EventInstanceDuration, typeof customProps>[]>(eventInstanceColumns)
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<EventInstanceDuration, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return isLoading ? (
    <GRCircularProgress />
  ) : (
    <>
      <Box sx={{ textAlign: 'center' }}>
        <EventHeader
          eventId={eventId}
          eventTypeId={eventTypeId}
          trackedGameId={currentTrackedGame?.game.id}
          duration={duration}
          onEventHighlighted={onEventHighlighted}
        />
      </Box>
      <TableContainer component={Card}>
        <GRTable
          rows={cappedEventInstanceDurations}
          columns={columns}
          rowIdKey="id"
          customProps={customProps}
          onColumnsUpdated={handleColumnsUpdate}
          isLoading={isLoading}
        />
      </TableContainer>
      {gamePerformanceDialogData && (
        <GamePerformanceDialog
          open={!!gamePerformanceDialogData}
          onClose={handlePerformanceDialogClose}
          appId={gamePerformanceDialogData!.appId}
          marketIso={gamePerformanceDialogData!.marketIso}
          gameName={gamePerformanceDialogData!.gameName}
          selectedVerticalMarks={gamePerformanceDialogSelectedUpdates}
          highlightedVerticalMarks={gamePerformanceDialogEventPeriodMarks}
          highlightedPeriod={gamePerformanceDialogData.highlightedPeriod}
          initialYAxisLeftConfig={gamePerformanceDialogData.initialYAxisLeftConfig}
          initialYAxisRightConfig={gamePerformanceDialogData.initialYAxisRightConfig}
          initialGranularity={gamePerformanceDialogData.initialGranularity}
          onChartClick={handleChartClick}
          selectedGame={currentTrackedGame}
          selectedInstanceDuration={initialSelectedPerformanceChartInterval}
          render={(granularity: GranularityValue, dateRange) => {
            return (
              <PerformanceDialogEventsContainer
                interval={getPerformanceDialogInstanceTableInterval(granularity)}
                dateRange={dateRange}
                parentEvents={trackedEvents}
                trackedGames={trackedGames}
                appId={gamePerformanceDialogData.appId}
                isLoading={isLoading}
                onEventHighlighted={onEventHighlighted}
              />
            )
          }}
        ></GamePerformanceDialog>
      )}
    </>
  )
}
